/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description Utils for Response Screen of Eligibility
 */
import _ from 'lodash';
import { covertDateToStringArray, DATE_PRESETS } from 'utils/dateParser';

const dataKeys: Record<string, keyof EligibilityRequests.IApiParams | 'startDate' | 'endDate' | 'startDatetime' | 'endDatetime'> = {
	payerId: 'payerIds',
	identifier: 'patientIdentifier',
	patientName: 'patientName',
	inquiryDate: 'inquiryDate',
	dateOfService: 'dateOfService',
	// custom
	startDate: 'startDate',
	endDate: 'endDate',
	startDatetime: 'startDatetime',
	endDatetime: 'endDatetime',
	// filter params
	provider: 'providerIds',
	coverageStatus: 'coverageStatus',
	locationId: 'locationIds',
	insuranceType: 'insuranceType',
	hasAdditionalPayers: 'hasAdditionalPayers',
};

const dataKeyLabels: Partial<Record<keyof EligibilityRequests.IApiParams, string>> = {
	payerIds: 'Payer',
	patientIdentifier: 'Patient Identifier',
	patientName: 'Patient Name',
	inquiryDate: 'Inquiry Date',
	dateOfService: 'Service Dates',
	// filter params
	providerIds: 'Provider/Organization',
	coverageStatus: 'Eligibility Status',
	locationIds: 'Service Location',
	insuranceType: 'Insurance Type',
	hasAdditionalPayers: 'Additional Insurance',
};

const EXPORT_OPTIONS = [
	{ value: covertDateToStringArray(DATE_PRESETS.monthToDate.value), label: DATE_PRESETS.monthToDate.label },
	{ value: covertDateToStringArray(DATE_PRESETS.lastMonth.value), label: DATE_PRESETS.lastMonth.label },
	{ value: covertDateToStringArray(DATE_PRESETS.lastThreeMonths.value), label: DATE_PRESETS.lastThreeMonths.label },
];

const dateType = {
	radio: 'radio' as EligibilityRequests.IExportDateRangeType,
	datePicker: 'date-picker' as EligibilityRequests.IExportDateRangeType,
};

const getPatientName = (name?: EligibilityRequests.IPatientName): string => {
	const nameArray = _.compact([name?.firstname, name?.middlename, name?.lastname]);
	if (_.isEmpty(nameArray)) {
		return '';
	}
	return _.join(nameArray, ' ');
};

const prepareDateRange = (date: [string, string]): EligibilityRequests.IDateRange => {
	return { startDate: _.first(date) || '', endDate: _.last(date) || '' };
};

const getServiceTypesLabel = (record: EligibilityRequests.IEligibilityData, serviceTypesOptions?: IMultiSelectOption[]): string => {
	if (_.isArray(record?.requestInfo?.serviceType)) {
		const matchingOptions = _.filter(serviceTypesOptions, (option) => _.includes(record.requestInfo?.serviceType, option.value));
		return _.map(matchingOptions, 'label').join(', ');
	} else {
		return _.find(serviceTypesOptions, { value: record.requestInfo?.serviceType })?.label || '';
	}
};

const getFilterLabel = (label: string, count: number): string => {
	if (count === 0) {
		return label;
	}
	return `(${count}) ${label}`;
};

const filterEmptyValue: (params: Record<string, any>) => Record<string, any> = (params) => {
	return _.pickBy(params, (value) => !_.isEmpty(value) || _.isBoolean(value));
};

export {
	dataKeys,
	dataKeyLabels,
	EXPORT_OPTIONS,
	dateType,
	getPatientName,
	prepareDateRange,
	getServiceTypesLabel,
	getFilterLabel,
	filterEmptyValue,
};
